import { useEffect, useRef, useState } from 'react'
import type {
  EventListener,
  MutedAutoplayConfiguration,
  PlayEvent,
} from 'theoplayer'
import * as THEOplayer from 'theoplayer'
import { useScript } from 'usehooks-ts'

import { config } from '@/config'
import { useAccessToken } from '@/core/auth'
import { captureError } from '@/core/errors'
import { useTranslation } from '@/core/i18n'
import { useMessage } from '@/core/messages'

const license = config.theoPlayerLicenseKey
const libraryLocation = config.theoPlayerLibraryLocation
const functionsEndpoint = config.functionsEndpoint

/**
 *  TheoPlayerでDRMリソースを再生するためのフック。
 */
type UseDRMPlayerProps = {
  onPlay?: EventListener<PlayEvent>
  source: {
    dashUri: string
    hlsUri: string
  }
  opts?: PlayerOptions
  isMagazine?: boolean
}

export const useDRMPlayer = ({
  source: { dashUri, hlsUri },
  onPlay = () => {},
  opts = { chromeless: false, mutedAutoplay: 'none' },
  isMagazine = false,
}: UseDRMPlayerProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [player, setPlayer] = useState<ReturnType<typeof initTheoPlayer>>(null)
  const { accessToken } = useAccessToken()
  const status = useScript(libraryLocation + '/THEOplayer.js')
  const [currentTime, setCurrentTime] = useState(0)
  const { showError } = useMessage()
  const { t } = useTranslation()

  useEffect(() => {
    if (!(ref.current && status === 'ready' && accessToken)) return () => {}

    const player = initTheoPlayer(ref, opts)
    if (!player) return () => {}

    player.source = {
      sources: [
        {
          src: dashUri,
          type: 'application/dash+xml',
          contentProtection: {
            playready: {
              licenseAcquisitionURL: `${functionsEndpoint}/drm/playready`,
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
            widevine: {
              licenseAcquisitionURL: `${functionsEndpoint}/drm/widevine`,
              headers: {
                'Content-Type': 'application/octet-stream',
                Accept: 'application/octet-stream',
                Authorization: `Bearer ${accessToken}`,
              },
            },
          },
        },
        {
          src: hlsUri,
          type: 'application/x-mpegURL',
          contentProtection: {
            fairplay: {
              licenseAcquisitionURL: `${functionsEndpoint}/drm/fairplay`,
              certificateURL: config.fairplayCertificateUrl,
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
          },
        },
      ],
    }

    //hidden play icon on android
    const videoElement = player.element.querySelector('video:not([src])')
    videoElement?.setAttribute('poster', 'noposter')

    if (opts.mutedAutoplay === 'all') {
      player.autoplay = true
    }

    player.addEventListener('play', onPlay)
    player.addEventListener('timeupdate', e => setCurrentTime(e.currentTime))

    player?.addEventListener('error', e => {
      captureError(e, 'useDRMPlayer')
      showError(t('theoPlayerError'))
    })
    player.addEventListener('ended', () => {
      // マガジンアイテムの場合はcurrentTimeを0に戻さない
      if (!isMagazine) {
        setCurrentTime(0)
      }
      player.pause()
    })

    setPlayer(player)

    return player.destroy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, status, accessToken])

  return { player, currentTime, ref, accessToken }
}

/**
 * プレイヤーの初期化
 */

export function initTheoPlayer(
  ref: React.RefObject<HTMLElement>,
  { chromeless, mutedAutoplay }: PlayerOptions
) {
  if (typeof window === 'undefined' || !window.THEOplayer) return null

  const Player = chromeless
    ? (window.THEOplayer.ChromelessPlayer as typeof THEOplayer.ChromelessPlayer)
    : (window.THEOplayer.Player as typeof THEOplayer.Player)

  if (ref.current) {
    return new Player(ref.current, {
      license,
      libraryLocation,
      mutedAutoplay,
    })
  }
  return null
}

type PlayerOptions = {
  /**
   * 標準プレイヤーのUIを使う場合はfalse, 標準プレイヤーを隠す場合はtrue
   */
  chromeless?: boolean
  /**
   * 無音自動再生する。画像として表示したいリソースに用いる
   */
  mutedAutoplay?: MutedAutoplayConfiguration
}
